import { Button, buttonVariants, Spinner, Text } from 'ui_2';
import { ArcadeTicketIcon } from '@components/ArcadeTicketIcon';
import { BiLinkExternal } from 'react-icons/bi';
import { RecordWithPlaintext } from '@puzzlehq/types';
import { useHasInjectedConnection } from '@puzzlehq/frontend-utils';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import { usePurchaseStore } from '@src/routes/$service/$productSKU/-purchaseStore';
import { useShallow } from 'zustand/react/shallow';
import { ProductSKU } from 'jigsaw';
import { RecordCount } from '@src/hooks/usePurchaseHelper';
import { JoinStatus } from '@src/hooks/usePartJoin';
import { useHasOrderedToday } from '@src/hooks/useArcadeStore';
import { GetProButton } from '../pro/GetProButton';

type PurchaseButtonProps = {
  productSKU: ProductSKU;
  hasPendingPurchases?: boolean;
  joinInfo: {
    joinRecordCount?: number;
    isLoadingJoinParts?: boolean;
    recordsToJoin?: RecordWithPlaintext[][];
    joinStatus?: JoinStatus;
    openJoinModal: () => void;
  };
  purchaseInfo: {
    recordCount?: RecordCount;
    partPurchaseRecords?: RecordWithPlaintext[];
    checked: boolean;
    fee?: number;
    programId?: string;
    functionId?: string;
  };
};

export const PurchaseButton = ({
  productSKU,
  hasPendingPurchases,
  joinInfo: {
    joinRecordCount,
    isLoadingJoinParts,
    recordsToJoin,
    joinStatus,
    openJoinModal,
  },
  purchaseInfo: {
    recordCount,
    partPurchaseRecords,
    checked,
    fee,
    programId,
    functionId,
  },
}: PurchaseButtonProps) => {
  const price = productSKU.price;
  const stock = productSKU.stock;

  const { account, partBalance, isPro, isPuzzleWalletWebview, partRecords } =
    useArcadeStoreState();

  const hasOrderedToday = useHasOrderedToday();
  const largestRecord: RecordWithPlaintext | undefined = partRecords?.[0];

  const { hasInjectedConnection, isLoading: isLoadingInjectedConnection } =
    useHasInjectedConnection();

  const [status, createEventResponse, purchase, _close] = usePurchaseStore(
    useShallow((state) => [
      state.status,
      state.createEventResponse,
      state.purchase,
      state.close,
    ])
  );

  const renderDailyOrderLimitReached = () => (
    <Button
      size='md'
      variant='primary'
      className='items-center gap-2'
      fullWidth
      disabled
    >
      Daily Purchase Limit Reached!
    </Button>
  );

  const renderDownloadButton = () => (
    <a
      href='https://puzzle.online/wallet'
      target='_blank'
      className={buttonVariants({
        size: 'md',
        variant: 'primary',
        className: 'w-full items-center gap-2',
      })}
    >
      Download Puzzle Wallet
      <BiLinkExternal />
    </a>
  );

  // todo add join functionality here!
  const renderLowBalanceButton = () => (
    <Button disabled={true} fullWidth>
      Low PART Balance
    </Button>
  );

  const renderJoinRequiredButton = () => (
    <Button
      disabled={
        recordsToJoin === undefined ||
        recordsToJoin.length === 0 ||
        isLoadingJoinParts
      }
      onClick={() => openJoinModal?.()}
      fullWidth
    >
      PART Join Required! Join {joinRecordCount} records.
    </Button>
  );

  const renderJoiningButton = () => (
    <Button disabled={true} fullWidth className='items-center gap-2'>
      Joining in Progress
      <Spinner />
    </Button>
  );

  const renderPurchaseButton = (disabled?: boolean) => (
    <Button
      className='items-center gap-2'
      fullWidth
      disabled={
        status === 'loading' ||
        (partBalance ?? 0) < productSKU.price ||
        largestRecord === undefined ||
        fee === undefined ||
        programId === undefined ||
        functionId === undefined ||
        checked === false ||
        disabled
      }
      onClick={() =>
        largestRecord &&
        programId &&
        functionId &&
        fee !== undefined &&
        recordCount !== undefined &&
        partPurchaseRecords !== undefined &&
        purchase({
          price: productSKU.price,
          programId,
          functionId,
          fee,
          skuId: productSKU.skuId,
          recordCount,
          partPurchaseRecords: partPurchaseRecords,
        })
      }
    >
      Purchase with <ArcadeTicketIcon /> {price}
      {status === 'loading' && <Spinner />}
    </Button>
  );

  const renderSoldOutButton = () => (
    <Button
      className='items-center gap-2 border border-gray-800'
      variant='gray'
      disabled
      fullWidth
    >
      Sold out
    </Button>
  );

  const renderPurchaseInProgressButton = () => (
    <Button
      className='items-center gap-2 border border-gray-800'
      variant='gray'
      disabled
      fullWidth
    >
      Purchase in progress
    </Button>
  );

  if (
    hasInjectedConnection === false &&
    isLoadingInjectedConnection === false
  ) {
    return renderDownloadButton();
  } else if (!account) {
    return renderPurchaseButton(true);
  } else if ((stock !== undefined && stock > 0) || status === 'loading') {
    if (isPro === false) {
      return <GetProButton />;
    } else if (hasOrderedToday) {
      return renderDailyOrderLimitReached();
    } else if (joinStatus === 'Joining' || isLoadingJoinParts) {
      return renderJoiningButton();
    } else if (recordCount === 'LOW') {
      return renderLowBalanceButton();
    } else if (recordCount === 'JOIN_REQUIRED') {
      return renderJoinRequiredButton();
    } else if (hasPendingPurchases) {
      return renderPurchaseInProgressButton();
    } else {
      return renderPurchaseButton();
    }
  } else {
    return renderSoldOutButton();
  }
};
